
import filters from "@/filters";
import { Component, Vue } from "vue-property-decorator";
import AppLayout from "@/layouts/AppLayout.vue";
import { Action, State } from "vuex-class";
import { AxiosError, AxiosResponse } from "axios";
import _ from "lodash";
import {
  AdminJobFairJobQuestionTypeEnum,
  EnumHelper,
  JobTypeEnum,
} from "@/constants/enum.constant";
import toast from "@/functions/toast.function";
import {
  formDataAppend,
  isValidUrl,
  onScroll,
} from "@/functions/global.function";
import ScanQr from "@/components/ScanQr.vue";
import moment from "moment";
import Loading from "@/components/Loading.vue";

interface CandidateCV {
  "id": number
  "candidate_id": number
  "name": "CV untuk Frontdesk"
  "created_at": string
  "updated_at": string
  "deleted_at": string | null
}

@Component({
  components: {
    Loading,
    ScanQr,
    AppLayout,
  },
  filters,
})
export default class CompanyDetail extends Vue {
  @Action("getLanguage", { namespace: "admin" })
  getLanguage: any;
  @Action("getAdminJobFairCandidateCompanyDetail", { namespace: "admin" })
  getAdminJobFairCandidateCompanyDetail: any;
  @Action("getAdminJobFairCandidateCompanyJob", { namespace: "admin" })
  getAdminJobFairCandidateCompanyJob: any;
  @Action("adminJobFairCandidateApply", { namespace: "admin" })
  adminJobFairCandidateApply: any;
  @Action("getCandidateCv", { namespace: "admin" })
  getCandidateCv!: ServiceCall<CandidateCV[]>;

  @State("locale")
  locale!: string;
  @State("candidate", { namespace: "admin" })
  candidate!: any;

  jobs: Array<any> = [];
  urls: Array<any> = [];
  languages: Array<any> = [];
  candidateCvs = Array<CandidateCV & { value: CandidateCV }>();
  company: any = null;
  job: any = null;
  cv: File | CandidateCV | null = null;
  language: any = null;
  isApply = false;
  step = 1;
  search = "";
  url = "";
  page = 1;
  isLastPage = false;
  isLoadingCompany = false;
  isLoadingJob = false;
  isLoadingApply = false;
  types = EnumHelper(JobTypeEnum);

  get isEn() {
    return this.locale === "en";
  }

  get isJobFairEnded() {
    if (this.company?.job_fair)
      return moment(moment())
        .utc()
        .isSameOrAfter(this.company?.job_fair.end_date);
    else return false;
  }
  get isOneDay() {
    return moment(this.company?.job_fair.start_date).isSame(
      this.company?.job_fair.end_date,
      "day"
    );
  }

  isShortText(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.SHORT_TEXT;
  }
  isLongText(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.LONG_TEXT;
  }
  isMultipleChoices(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.MULTIPLE_CHOICES;
  }
  isCheckbox(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.CHECKBOX;
  }
  isDropdown(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.DROPDOWN;
  }
  isFile(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.FILE;
  }
  isScale(data: any) {
    return data.type === AdminJobFairJobQuestionTypeEnum.SCALE;
  }

  created() {
    this.getData();
    this.getJobData();
    this.getCandidateCv({
      success: ({ data: { data } }) => this.candidateCvs = data.map((v) => ({ ...v, value: v }))
    })

    this.getLanguage({
      success: (response: AxiosResponse) => {
        this.languages = response.data.data;
      },
    });
  }

  mounted() {
    setTimeout(() => {
      onScroll(".candidate-company-jobs", this.getJobData);
    }, 500);
  }

  getData() {
    this.isLoadingCompany = true;
    this.getAdminJobFairCandidateCompanyDetail({
      params: {
        jobFairSlug: this.$route.params.slug,
        slug: this.$route.params.company_slug,
      },
      success: (response: AxiosResponse) => {
        this.company = response.data.data;
      },
      fail: (error: AxiosError) => {
        if (error.response?.data.status !== 401)
          this.$router.push({
            name: "not-found-with-job-fair",
          });
      },
      finally: () => {
        this.isLoadingCompany = false;
      },
    });
  }

  getJobData(isReload: boolean = false) {
    if (isReload) {
      this.jobs = [];
      this.page = 1;
      this.isLastPage = false;
    }
    if (!this.isLoadingJob && !this.isLastPage) {
      this.isLoadingJob = true;
      this.getAdminJobFairCandidateCompanyJob({
        params: {
          jobFairSlug: this.$route.params.slug,
          slug: this.$route.params.company_slug,
          page: this.page,
          name: this.search,
        },
        success: (response: AxiosResponse) => {
          const isLastPage = response.data.data.last_page === this.page;
          this.jobs = [...this.jobs, ...response.data.data.data];
          this.page = isLastPage ? this.page : this.page + 1;
          this.isLastPage = isLastPage;
        },
        finally: () => {
          this.isLoadingJob = false;
        },
      });
    }
  }

  websocketCompany() {
    (window as any).socket.send(
      JSON.stringify({
        slug: this.$route.params.slug,
        chart: "company",
      })
    );
  }

  websocketApplication() {
    (window as any).socket.send(
      JSON.stringify({
        slug: this.$route.params.slug,
        chart: "application",
      })
    );
  }

  websocketApplicationStatus() {
    (window as any).socket.send(
      JSON.stringify({
        slug: this.$route.params.slug,
        chart: "application-status",
      })
    );
  }

  addUrl() {
    let value = this.url.toLowerCase();
    if (value) {
      if (value.indexOf("http://") === 0 || value.indexOf("https://") === 0) {
        if (!isValidUrl(value)) {
          toast.warning("Invalid url.");
          return false;
        }
        this.urls.push(value);
        this.url = "";
        return true;
      } else {
        toast.warning("Link harus diawali dengan http:// atau https://");
        return false;
      }
    }
  }

  debounce(callback: any) {
    _.debounce(callback, 500)();
  }

  setApply() {
    if (!this.job?.is_candidate_applied) this.isApply = true;
  }

  getExt(name: string, separator: string = ".") {
    const separatedName = name.split(separator);
    return separatedName[separatedName.length - 1];
  }

  apply() {
    switch (this.step) {
      case 1: {
        if (this.isJobFairEnded) {
          toast.warning(
            this.isEn ? "Job fair has ended" : "Bursa kerja telah berakhir"
          );
          return;
        }
        if (this.url) {
          if (!this.addUrl()) return;
        }
        if (!this.cv) {
          toast.warning(this.isEn ? "Please upload CV" : "Silahkan unggah CV");
          return;
        } else if (this.cv instanceof File) {
          let ext = this.getExt(this.cv?.name);
          const extensions = [
            "pdf",
            "jpg",
            "jpeg",
            "png",
            "bmp",
            "gif",
            "svg",
            "webp",
            "doc",
            "docx",
            "zip",
            "rar",
          ];
          if (!extensions.includes(ext.toLowerCase())) {
            toast.warning(
              this.isEn
                ? `Please upload file with the correct format (${extensions.join(
                  ", "
                )})`
                : `Silahkan unggah file dengan format yang sesuai (${extensions.join(
                  ", "
                )})`
            );
            return false;
          }
          if (Math.round(this.cv?.size / 1024) > 10240) {
            toast.warning(
              this.isEn
                ? "The maximum file size that can be uploaded is 10MB"
                : "Ukuran file yang dapat diunggah maksimum 10MB"
            );
            return false;
          }
        }
        this.step++;
        break;
      }
      case 2: {
        if (!this.language || this.language.length < 1) {
          toast.warning(
            this.isEn
              ? "Please select at least one language."
              : "Silahkan pilih paling tidak satu bahasa."
          );
          return false;
        }
        let count = 1;
        const canNext = this.job?.sections.every((section, idx) => {
          return section.questions.every((question, index) => {
            if (question.is_required && !question.answer) {
              toast.warning(`Anda belum menjawab pertanyaan ke-${count}.`);
              return false;
            }
            if (
              this.isMultipleChoices(question) &&
              question.answer ===
                question.answers.find((value) => value.is_other).id &&
              !question.other_answer
            ) {
              toast.warning(`Anda belum menjawab pertanyaan ke-${count}.`);
              return false;
            }
            if (
              this.isCheckbox(question) &&
              question.answer &&
              question.answer.includes(
                question.answers.find((value) => value.is_other).id
              ) &&
              !question.other_answer
            ) {
              toast.warning(`Anda belum menjawab pertanyaan ke-${count}.`);
              return false;
            }
            if (
              this.isDropdown(question) &&
              question.answer.id ===
                question.answers.find((value) => value.is_other).id &&
              !question.other_answer
            ) {
              toast.warning(`Anda belum menjawab pertanyaan ke-${count}.`);
              return false;
            }
            count++;
            return true;
          });
        });
        if (!canNext) return false;
        const formData = new FormData();
        formData.append("admin_job_fair_job_id", this.job?.id);
        if(this.cv instanceof File) formData.append("attachment", this.cv);
        else if(this.cv) formData.append("cv_id", this.cv.id.toString());
        formData.append("url", this.urls.join(","));
        formDataAppend(
          formData,
          this.language?.map((value) => ({ language_id: value.id })),
          "languages"
        );
        formDataAppend(
          formData,
          this.job?.sections.map((section) => ({
            ...section,
            questions: section.questions.map((question) => {
              if (this.isScale(question)) {
                return {
                  ...question,
                  answer: question.answers.find(
                    (answer) => parseInt(answer.name) === question.answer
                  ).id,
                };
              }
              return question;
            }),
          })),
          "sections"
        );
        this.isLoadingApply = true;
        this.adminJobFairCandidateApply({
          params: formData,
          success: (response: AxiosResponse) => {
            this.cv = null;
            this.url = "";
            this.urls = [];
            this.job = null;
            this.search = "";
            this.getJobData(true);
            this.websocketCompany();
            this.websocketApplication();
            this.websocketApplicationStatus();
          },
          finally: () => {
            this.isLoadingApply = false;
          },
        });
        break;
      }
    }
  }
}
